import { html } from '@acme/b2b/utils';
import { NoticesComponentModule } from '@b2b/common/components/notices';
import { DataGridUserPreferencesLoadedData, GridDefinitionFactory } from '@b2b/common/factories/gridDefinition/types';
import { GridStateFactory } from '@b2b/common/factories/gridState/types';
import { NoticeFactory } from '@b2b/common/factories/notice';
import { FormatDateFilterModule, parseUrl } from '@b2b/common/filters';
import { ThemesData } from '@b2b/common/resolvers';
import { CustomerFormResponse } from '@b2b/common/services/api/ordersApi/types';
import { ApiService } from '@b2b/common/services/api/types';
import { CalendarService } from '@b2b/common/services/calendar';
import { ContentHeaderService, ContentHeaderServiceModule } from '@b2b/common/services/contentHeader';
import { CustomerService, CustomerServiceModule } from '@b2b/common/services/customer';
import { PermissionsService } from '@b2b/common/services/permissions';
import { ThemesHelperService } from '@b2b/common/services/themesHelper';
import { UserPreferencesService } from '@b2b/common/services/userPreferences/types';
import { ensureArray } from '@b2b/common/utils/coerce-array';
import {
  ACMEListResponse,
  ACMEResponse,
  ColumnDef,
  ContentHeaderProps,
  extractACMEErrorMessage,
  extractACMEPayload,
  TODO,
  UIBModalService,
} from '@b2b/types/core';
import { Customer } from '@b2b/types/customer';
import uiRouter, { StateService } from '@uirouter/angularjs';
import angular, { IHttpResponse, IQService, IScope, ITimeoutService } from 'angular';
import moment from 'moment-timezone';
import {
  CustomersIndividualsListService,
  CustomersIndividualsListServiceModule,
} from '../../list/services/customers-individuals-list.service';
import { rawFiltersSchema } from '../../list/types';
import {
  CustomersIndividualsDetailServiceFactory,
  CustomersIndividualsDetailServiceModule,
} from '../services/customers-individuals-detail.service';
import { CustomerIndividualDetailTab, CustomerIndividualMembershipListItem, CustomerVM } from '../types';

class CustomersIndividualsDetailController {
  static $inject = [
    '$scope',
    '$q',
    '$state',
    '$timeout',
    '$uibModal',
    'ApiService',
    'CalendarService',
    'UserPreferencesService',
    'CustomerService',
    'ValidateService',
    'PermissionsService',
    'NoticeFactory',
    'GridStateFactory',
    'GridDefinitionFactory',
    'CustomersIndividualsListService',
    'CustomersIndividualsDetailServiceFactory',
    'ThemesHelperService',
    'ContentHeaderService',
  ];

  ThemesData?: { data: ACMEResponse<ThemesData> };
  membershipsData: Record<number, TODO> = {};

  customersIndividualsDetailService = this.customersIndividualsDetailServiceFactory.createInstance();
  dateTimeFormat = '';
  model = {} as TODO;
  addresses = [] as TODO[];
  memberships = [] as TODO[];
  attributes = {} as TODO;
  solicitationCodes = {} as TODO;
  formsResponsesList = [] as TODO[];
  formsResponsesListStatus: 'initial' | 'pending' | 'fulfilled' = 'initial';

  possiblyDuplicateCustomers = [] as Customer[];

  tab: CustomerIndividualDetailTab = '';

  errors = {} as Record<string, TODO>;

  customer = {} as CustomerVM;
  titles: { title: string; name: string }[] = [];

  loading = false;
  initLoading = false;

  isMembershipLoading = false;
  enableCustomerMerge = false;

  customerIdDetails = {
    columns: 1,
    columnWidth: 4,
    data: 'customersIndividualsDetail.model',
    include: [
      'customerSource',
      'customerId',
      'externalCustomerImportId',
      'externalId',
      'externalCustomerId',
      'orgName',
    ],
    mappings: {
      customerId: 'ACME Customer Number',
      externalCustomerImportId: 'External Customer Import ID',
      externalId: 'External ID',
      externalCustomerId: 'External Customer ID',
      orgName: 'Organization',
    },
  };

  customerDetails = {
    columns: 3,
    columnWidth: 4,
    data: 'customersIndividualsDetail.model',
    include: ['prefix', 'firstName', 'middleName', 'lastName', 'email', 'phoneNumber'],
    mappings: {
      prefix: 'Title',
    },
  };

  notesDetails = {
    columns: 1,
    columnWidth: 12,
    data: 'customersIndividualsDetail.model',
    include: ['notes'],
    mappings: {
      notes: 'Customer Notes',
    },
  };

  customerMetaDetails = {
    columns: 2,
    columnWidth: 4,
    data: 'customersIndividualsDetail.model',
    include: ['createdOn', 'createdBy', 'updatedOn', 'updatedBy'],
  };

  attributesGridOptions = {
    pageSize: 15,
    paginate: true,
    useExternalPagination: true,
    useExternalSorting: true,
    multiselect: false,
    showFooter: false,
    columnDefs: [],
  };

  membershipGridOptions = {
    rowHeight: 40,
    useAccessibleGrid: true,
    enablePagination: false,
    showGridFooter: false,
    data: 'customersIndividualsDetail.memberships',
    rowClasses: "{ 'ui-grid-row-header-cell': col.isRowHeader, 'data-grid-group-by--row': true  }",
    appScopeProvider: {
      onDataGridPaginate: (newPage, pageSize, sortField, sortDirection) => {
        this.refreshMembershipsGrid();
      },
      onSetAsLatest: (item) => {
        this.onSetAsLatest(item);
      },
      onIdClick: (item) => {
        this.onIdClick(item);
      },
    },
    columnDefs: [
      {
        field: 'id',
        displayName: 'ID',
        enableHiding: false,
        enableSorting: false,
        onCellNavClick: (row, col) => {
          this.membershipGridOptions.appScopeProvider.onIdClick(row.entity);
        },
        cellTemplate: `
          <div class="ui-grid-cell-contents" ng-class="col.colIndex()">
            <span data-ng-if="row.entity.hasOwnProperty('rowName')">{{ row.entity.rowName }}</span>
            <a data-ng-if="!row.entity.hasOwnProperty('rowName')" class="clickable" data-ng-click="grid.appScope.onIdClick(row.entity)" role="button">
              <span class="sr-only">Go to membership </span>{{ row.entity.id }}
            </a>
          </div>`,
        visible: true,
      },
      {
        field: 'membershipCategory',
        displayName: 'ACME Level',
        visible: true,
        enableSorting: false,
      },
      {
        field: 'offeringName',
        displayName: 'Offering Name',
        visible: true,
        enableSorting: false,
      },
      {
        field: 'auxiliaryMembership',
        displayName: 'Is Aux',
        visible: false,
        enableSorting: false,
      },
      {
        field: 'isGift',
        displayName: 'Is Gift',
        visible: true,
        enableSorting: false,
      },
      {
        field: 'membershipStanding',
        displayName: 'Membership Standing',
        visible: true,
        enableSorting: false,
      },
      {
        field: 'externalMembershipId',
        displayName: 'External Membership ID',
        visible: false,
        enableSorting: false,
      },
      {
        field: 'membershipJoinedDateDisplay',
        displayName: 'Joined',
        visible: false,
        enableSorting: false,
      },
      {
        field: 'membershipStartDateDisplay',
        displayName: 'Start Date',
        visible: true,
        enableSorting: false,
      },
      {
        field: 'membershipExpirationDateDisplay',
        displayName: 'Expiration Date',
        visible: true,
        enableSorting: false,
      },
      {
        field: 'isLatest',
        displayName: 'Latest',
        visible: true,
        enableSorting: false,
      },
    ] as ColumnDef[],
  };

  mergeHistoryGridOptions = {
    rowHeight: 40,
    useAccessibleGrid: true,
    enableSorting: false,
    enableGridMenu: false,
    enablePagination: false,
    showGridFooter: false,
    data: 'customersIndividualsDetail.customer.mergeHistory',
    appScopeProvider: {
      onDataGridPaginate: (newPage, pageSize, sortField, sortDirection) => {
        this.refreshMergeHistoryGrid();
      },
    },
    columnDefs: [
      {
        field: 'customerId',
        displayName: 'ACME Customer Number',
        visible: true,
        enableSorting: false,
      },
      {
        field: 'externalCustomerImportId',
        displayName: 'External Customer Import ID',
        visible: true,
        enableSorting: false,
      },
      {
        field: 'firstName',
        displayName: 'First Name',
        visible: true,
        enableSorting: false,
      },
      {
        field: 'lastName',
        displayName: 'Last Name',
        visible: true,
        enableSorting: false,
      },
      {
        field: 'email',
        displayName: 'Email',
        visible: true,
        enableSorting: false,
      },
    ] as ColumnDef[],
  };

  formsFilterDateType = [
    { type: 'createdOn', name: 'Created On' },
    { type: 'updatedOn', name: 'Updated On' },
  ];
  formsSelectedFilterDateType = this.formsFilterDateType[0];

  today = moment();
  minDate = this.today.clone().startOf('day').subtract(1, 'year');
  maxDate = this.today.clone().endOf('day');

  formsQueryParams = {
    filterType: this.formsSelectedFilterDateType.type,
    startDate: this.minDate.clone().toISOString(),
    endDate: this.maxDate.clone().toISOString(),
  };
  formsGridOptions = {
    gridState: new this.gridStateFactory(),
    optionsOverride: {
      appScopeProvider: {
        dateTimeFormat: this.dateTimeFormat,
        onDataGridPaginate: (newPage, pageSize, sortField, sortDirection) => {
          this.formsGridOptions.gridState.updateGridState({ page: newPage, pageSize, sortField, sortDirection });

          this.loadFormsTabData();
        },
        onDataGridSortChange: (grid, columns, page) => {
          const data = columns[0];

          if (data) {
            this.formsGridOptions.gridState.updateGridState({
              page,
              sortField: data.field,
              sortDirection: data.sort.direction,
            });

            this.loadFormsTabData();
          }
        },
        onGridDoubleClick: (row: { entity: TODO }) => {
          if (this.permissionsService.check('FORM_RESPONSE_VIEW')) {
            this.$state.go('application.orders.detail.forms.detail', {
              orderId: row.entity.orderId,
              responseId: row.entity.formResponseId,
            });
          }
        },
        viewOrder: (row: { entity: TODO }) => {
          this.$state.go('application.orders.detail.summary', {
            orderId: row.entity.orderId,
          });
        },
        viewItem: (row: { entity: TODO }) => {
          switch (row.entity.formCategoryType) {
            case 'event': {
              this.$state.go('application.programsInstance', { id: row.entity.itemId });
              break;
            }
            case 'membership': {
              this.$state.go('application.membership.detail', { id: row.entity.membershipId });
              break;
            }

            case 'donation':
            case 'addon':
            default: {
              break;
            }
          }

          return;
        },
      },
      rowHeight: 40,
      useAccessibleGrid: true,
      data: 'customersIndividualsDetail.formsResponsesList',
      columnDefs: [
        {
          field: 'createdOn',
          displayName: 'Create Date',
          visible: true,
          cellTemplate: `<div class="ui-grid-cell-contents">{{ row.entity.createdOn | formatDate: grid.appScope.dateTimeFormat }}</div>`,
        },
        {
          field: 'updatedOn',
          displayName: 'Update Date',
          visible: true,
          cellTemplate: `<div class="ui-grid-cell-contents">{{ row.entity.updatedOn | formatDate: grid.appScope.dateTimeFormat }}</div>`,
          sort: {
            direction: 'desc',
          },
        },
        {
          field: 'formCategoryType',
          displayName: 'Type',
          visible: true,
          colId: 'formCategoryType',
          enableSorting: false,
          cellTemplate: `<div class="ui-grid-cell-contents"><span class="text-capitalize">{{ row.entity.formCategoryType }}</span></div>`,
        },
        {
          field: 'itemName',
          displayName: 'Event Template Name',
          visible: true,
          colId: 'itemName',
          enableSorting: false,
          onCellNavClick: (row, col) => {
            this.formsGridOptions.optionsOverride.appScopeProvider.viewItem(row);
          },
          cellTemplate: `
            <div class="clickable ui-grid-cell-contents" ng-class="col.colIndex()">
              <a data-ng-click="grid.appScope.viewItem(row)" role="link">
                <span class="sr-only">View Event </span>{{ row.entity.itemName }}<span class="sr-only"> Details</span>
              </a>
            </div
          `,
        },
        {
          field: 'membershipId',
          displayName: 'Membership ID',
          visible: true,
          colId: 'itemName',
          enableSorting: false,
          onCellNavClick: (row, col) => {
            this.formsGridOptions.optionsOverride.appScopeProvider.viewItem(row);
          },
          cellTemplate: `
            <div class="clickable ui-grid-cell-contents" ng-class="col.colIndex()">
              <a data-ng-click="grid.appScope.viewItem(row)" role="link">
                <span class="sr-only">View Membership ID</span> {{ row.entity.membershipId }}<span class="sr-only"> Details</span>
              </a>
            </div>
          `,
        },
        {
          field: 'membershipLevel',
          displayName: 'Membership Level',
          visible: true,
          colId: 'membershipLevel',
          enableSorting: false,
        },
        {
          field: 'membershipOffering',
          displayName: 'Membership Offering',
          visible: false,
          colId: 'membershipOffering',
          enableSorting: false,
        },
        {
          field: 'membershipLifecycle',
          displayName: 'Membership Lifecycle',
          visible: false,
          colId: 'membershipLifecycle',
          enableSorting: false,
        },
        {
          field: 'formName',
          displayName: 'Form Name',
          visible: true,
          colId: 'formName',
          enableSorting: false,
        },
        {
          field: 'formNumber',
          displayName: 'Response Number',
          visible: true,
          colId: 'formNumber',
          enableSorting: false,
        },
        {
          field: 'orderId',
          displayName: 'Order ID',
          visible: true,
          colId: 'orderId',
          enableSorting: false,
          onCellNavClick: (row, col) => {
            this.formsGridOptions.optionsOverride.appScopeProvider.viewOrder(row);
          },
          cellTemplate: `
            <div class="clickable ui-grid-cell-contents" ng-class="col.colIndex()">
              <a data-ng-click="grid.appScope.viewOrder(row)" role="link">
                View<span class="sr-only"> Order {{ row.entity.orderId }} Details</span>
              </a>
            </div>
          `,
        },
      ] as TODO[],
    },
  };

  codesGridOptions = {
    pageSize: 15,
    paginate: true,
    useExternalPagination: true,
    useExternalSorting: true,
    multiselect: false,
    showFooter: false,
    columnDefs: [],
  };

  notice = new this.noticeFactory();

  addressGridDefinition = new this.gridDefinitionFactory();
  addressGridName = this.$state.current.name + '.grid-0';

  membershipGridDefinition = new this.gridDefinitionFactory();
  membershipsGridName = this.$state.current.name + '.grid-1';

  mergeHistoryGridDefinition = new this.gridDefinitionFactory();
  mergeHistoryGridName = this.$state.current.name + '.grid-merge-history';

  formsGridDefinition = new this.gridDefinitionFactory();
  formsGridName = this.$state.current.name + '.grid-3';

  viewId = this.$state.current.name;

  addressGridOptions = {
    rowHeight: 40,
    useAccessibleGrid: true,
    data: 'customersIndividualsDetail.addresses',
    appScopeProvider: {
      onDataGridPaginate: (newPage, pageSize, sortField, sortDirection) => {
        this.refreshAddressesGrid();
      },
      onEditClick: (item) => {
        this.editAddress(item);
      },
    },
    columnDefs: [
      {
        field: 'type',
        displayName: 'Type',
        visible: true,
        enableSorting: false,
      },
      {
        field: 'streetAddress1',
        displayName: 'Address 1',
        visible: true,
        enableSorting: false,
      },
      {
        field: 'streetAddress2',
        displayName: 'Address 2',
        visible: true,
        enableSorting: false,
      },
      {
        field: 'city',
        displayName: 'City',
        visible: true,
        enableSorting: false,
      },
      {
        field: 'state',
        displayName: 'State',
        visible: true,
        enableSorting: false,
      },
      {
        field: 'zipCode',
        displayName: 'ZIP / Postal Code',
        visible: true,
        enableSorting: false,
      },
      {
        field: 'country',
        displayName: 'Country',
        visible: true,
        enableSorting: false,
      },
      {
        field: 'id',
        displayName: 'ID',
        visible: false,
        enableSorting: false,
      },
    ] as ColumnDef[],
  };

  constructor(
    private $scope: IScope & { customersIndividuals: TODO; customersIndividualsDetail: TODO },
    private $q: IQService,
    private $state: StateService,
    private $timeout: ITimeoutService,
    private $uibModal: UIBModalService,
    private apiService: ApiService,
    private calendarService: CalendarService,
    private userPreferencesService: UserPreferencesService,
    private customerService: CustomerService,
    private ValidateService: TODO,
    private permissionsService: PermissionsService,
    private noticeFactory: NoticeFactory,
    private gridStateFactory: GridStateFactory,
    private gridDefinitionFactory: GridDefinitionFactory,
    private customersIndividualsListService: CustomersIndividualsListService,
    private customersIndividualsDetailServiceFactory: CustomersIndividualsDetailServiceFactory,
    private themesHelperService: ThemesHelperService,
    private contentHeaderService: ContentHeaderService,
  ) {
    this.$scope.customersIndividualsDetail = this;
  }

  $onInit() {
    if (this.ThemesData && this.ThemesData.data.success) {
      this.themesHelperService.setModel(this.ThemesData.data.payload);
      this.enableCustomerMerge = this.themesHelperService.getFeatureFlag('enableCustomerMerge');
    }

    if (this.$state.params) {
      this.getCustomer();
    }

    if (this.isEditMode()) {
      this.initEditModeSettings();
    }

    this.addressGridDefinition.init(
      this.addressGridName,
      this.addressGridOptions,
      this.viewId,
      this.$state.current.data.enablePreferences,
    );

    this.membershipGridDefinition.init(this.membershipsGridName, this.membershipGridOptions, this.viewId, false);

    this.formsGridDefinition.init(
      this.formsGridName,
      this.formsGridOptions.optionsOverride,
      this.viewId,
      this.$state.current.data.enablePreferences,
    );

    this.mergeHistoryGridDefinition.init(this.mergeHistoryGridName, this.mergeHistoryGridOptions, this.viewId, false);

    this.initMembershipGrid();
    this.initFormsGrid();
    this.initHeader({ loading: true });

    this.setDateTimeFormat();
  }

  $onDestroy() {
    this.customersIndividualsDetailService.reset();
  }

  getStateParams() {
    return {
      id: (this.$state.params.id as string) ?? '',
      accountId: this.$state.params.accountId as string | undefined,
    };
  }

  initHeader(options: { loading: boolean }) {
    this.initLoading = options.loading;

    this.contentHeaderService.update({
      title: 'Customer Details',
      buttonLeft: {
        label: this.isEditMode() ? 'Cancel' : '< Back',
        action: () => {
          if (this.isEditMode()) {
            this.showCancelModal();
          } else {
            window.history.go(-1);
          }
        },
      },
      buttonRight: this.permissionsService.check(['CUSTOMERS_UPDATE'])
        ? {
            label: this.isEditMode() ? 'Update' : 'Edit',
            permissions: ['CUSTOMERS_UPDATE'],
            isDisabled: options.loading,
            action: () => {
              if (this.isEditMode()) {
                this.updateCustomer();
              } else {
                this.$state.go(
                  'application.customers.individuals.edit',
                  { id: this.getStateParams().id },
                  { location: 'replace' },
                );
              }
            },
          }
        : null,
    } as ContentHeaderProps);
  }

  initEditModeSettings() {
    this.titles = this.customerService.getTitles();

    Object.assign(this.customerDetails, {
      showEmpty: true,
      templates: {
        prefix: {
          getScope: this.$scope,
          permissions: 'CUSTOMERS_UPDATE',
          template:
            '\n' +
            '<div data-combobox \n' +
            'class="customers-individuals-detail-input"\n' +
            'data-items="customersIndividualsDetail.titles"\n' +
            'data-ng-model="customersIndividualsDetail.customer.selectedTitle"\n' +
            'data-name="customers-individuals-edit-title"\n' +
            'data-readonly="true"\n' +
            'data-append-to-body="true"\n' +
            'label="Title"></div>',
        },
        firstName: {
          getScope: this.$scope,
          permissions: 'CUSTOMERS_UPDATE',
          template:
            '\n' +
            '<div data-input-field-with-label\n' +
            'class="customers-individuals-detail-input"\n' +
            'data-label="First Name"\n' +
            'data-name="customers-individuals-edit-first-name"\n' +
            'data-field-name="customers-individuals-edit-first-name"\n' +
            'type="text"\n' +
            'data-model="customersIndividualsDetail.customer.firstName"\n' +
            'data-required="true"\n' +
            'data-errors="customersIndividualsDetail.errors">\n' +
            '</div>',
        },
        middleName: {
          getScope: this.$scope,
          permissions: 'CUSTOMERS_UPDATE',
          template:
            '\n' +
            '<div data-input-field-with-label\n' +
            'class="customers-individuals-detail-input"\n' +
            'data-label="Middle Name"\n' +
            'data-name="customers-individuals-edit-middle-name"\n' +
            'data-field-name="customers-individuals-edit-middle-name"\n' +
            'type="text"\n' +
            'data-model="customersIndividualsDetail.customer.middleName"\n' +
            'data-errors="customersIndividualsDetail.errors">\n' +
            '</div>',
        },
        lastName: {
          getScope: this.$scope,
          permissions: 'CUSTOMERS_UPDATE',
          template:
            '\n' +
            '<div data-input-field-with-label\n' +
            'class="customers-individuals-detail-input"\n' +
            'data-label="Last Name"\n' +
            'data-name="customers-individuals-edit-last-name"\n' +
            'data-field-name="customers-individuals-edit-last-name"\n' +
            'type="text"\n' +
            'data-model="customersIndividualsDetail.customer.lastName"\n' +
            'data-required="true"\n' +
            'data-errors="customersIndividualsDetail.errors">\n' +
            '</div>',
        },
        email: {
          getScope: this.$scope,
          permissions: 'CUSTOMERS_UPDATE',
          template:
            '\n' +
            '<div data-input-field-with-label\n' +
            'class="customers-individuals-detail-input"\n' +
            'data-label="Email"\n' +
            'data-name="customers-individuals-edit-email"\n' +
            'data-field-name="customers-individuals-edit-email"\n' +
            'type="text"\n' +
            'data-model="customersIndividualsDetail.customer.email"\n' +
            'data-errors="customersIndividualsDetail.errors">\n' +
            '</div>',
        },
        phoneNumber: {
          getScope: this.$scope,
          permissions: 'CUSTOMERS_UPDATE',
          template:
            '\n' +
            '<div data-input-field-with-label\n' +
            'class="customers-individuals-detail-input"\n' +
            'data-label="Phone Number"\n' +
            'data-name="customers-individuals-edit-phone"\n' +
            'data-field-name="customers-individuals-edit-phone"\n' +
            'type="text"\n' +
            'data-model="customersIndividualsDetail.customer.phoneNumber"\n' +
            'data-errors="customersIndividualsDetail.errors">\n' +
            '</div>',
        },
      },
      hideTitles: ['prefix', 'suffix', 'firstName', 'middleName', 'lastName', 'email', 'phoneNumber', 'phoneNotes'],
    });

    Object.assign(this.notesDetails, {
      showEmpty: true,
      templates: {
        notes: {
          getScope: this.$scope,
          permissions: 'CUSTOMERS_UPDATE',
          template: html`
            <div
              data-textarea-field-with-label
              class="customers-individuals-detail-input"
              data-label="Customer Notes"
              data-name="customers-individuals-edit-customer-note"
              data-field-name="customers-individuals-edit-customer-note"
              data-model="customersIndividualsDetail.customer.notes"
              data-maxlength="2000"
              data-rows="4"
              data-errors="customersIndividualsDetail.errors"
            ></div>
          `,
        },
      },
      hideTitles: ['notes'],
    });

    this.addressGridOptions.columnDefs.unshift({
      field: 'edit',
      displayName: 'Edit',
      onCellNavClick: (row, col) => {
        this.addressGridOptions.appScopeProvider.onEditClick(row.entity);
      },
      cellTemplate: `
        <div class="ui-grid-cell-contents" ng-class="col.colIndex()">
          <a class="clickable" data-ng-click="grid.appScope.onEditClick(row.entity)" role="button">
            <span>Edit<span class="sr-only"> address</span></span>
          </a>
        </div>`,
      enableHiding: false,
      enableSorting: false,
      width: 40,
    } as ColumnDef);
  }

  initMembershipGrid() {
    if (this.permissionsService.check(['MEMBERSHIP_UPDATE'])) {
      this.membershipGridOptions.columnDefs.push({
        field: 'setAsLatest',
        displayName: 'Set as Latest',
        visible: true,
        onCellNavClick: (row, col) => {
          this.membershipGridOptions.appScopeProvider.onSetAsLatest(row.entity);
        },
        cellTemplate: `<div class="ui-grid-cell-contents" ng-class="col.colIndex()"><a class="clickable" data-ng-show="row.entity.id && row.entity.membershipStanding !== \'new\' && row.entity.membershipStanding !== \'archived\'" data-ng-click="grid.appScope.onSetAsLatest(row.entity)" role="button">Set <span class="sr-only">membership with ID {{ row.entity.id }} </span>as Latest</a></div>`,
        enableHiding: false,
        enableSorting: false,
      } as ColumnDef);
    }
  }

  initFormsGrid() {
    if (this.permissionsService.check('FORM_RESPONSE_VIEW')) {
      this.formsGridOptions.optionsOverride.columnDefs.unshift({
        field: 'View',
        headerName: 'View',
        width: 60,
        onCellNavClick: (row, col) => {
          this.formsGridOptions.optionsOverride.appScopeProvider.onGridDoubleClick(row);
        },
        cellTemplate: `<div class="clickable ui-grid-cell-contents" ng-class="col.colIndex()"><a data-ng-click="grid.appScope.onGridDoubleClick(row)" role="link">View<span class="sr-only"> Details</span></a></div>`,
        enableHiding: false,
        enableSorting: false,
      });
    }

    this.$scope.$on('DataGrid::UserPreferencesLoaded', (e, data: DataGridUserPreferencesLoadedData) => {
      if (data.gridName !== this.formsGridName) {
        return;
      }

      const props = this.formsGridDefinition.getGridState();
      this.formsGridOptions.gridState.updateGridState(props);
    });
  }

  isEditMode() {
    return this.$state.current.name === 'application.customers.individuals.edit';
  }

  isTab(tab: CustomerIndividualDetailTab) {
    return this.tab === tab;
  }

  setTab(tab: CustomerIndividualDetailTab) {
    this.tab = tab;
    this.refreshGrids();
    this.loadTabData();
  }

  loadTabData() {
    switch (this.tab) {
      case 'forms': {
        this.loadFormsTabData();
        break;
      }

      default: {
        // noop
        break;
      }
    }
  }

  loadFormsTabData() {
    switch (this.formsResponsesListStatus) {
      case 'initial':
      case 'fulfilled': {
        this.notice.hideErrorMessages();
        this.formsResponsesListStatus = 'pending';

        const props = this.formsGridOptions.gridState.getGridState();

        this.apiService.orders.forms.responses
          .customer(
            { customerId: this.getStateParams().id },
            {
              page: props.page,
              pageSize: props.pageSize,
              sortDirection: props.sortDirection,
              sortField: props.sortField,
              startDate: this.formsQueryParams.startDate,
              endDate: this.formsQueryParams.endDate,
              filterType: this.formsQueryParams.filterType,
            },
          )
          .then(extractACMEPayload())
          .then((response) => response.list)
          .then((response) => this.getFormsResponses(response))
          .then((processedFormResponses) => {
            this.formsResponsesList = processedFormResponses;
          })
          .catch((response: IHttpResponse<unknown>) => {
            this.notice.showErrorMessages([{ message: extractACMEErrorMessage(response.data) }]);
            this.formsResponsesList = [];
          })
          .finally(() => {
            this.formsResponsesListStatus = 'fulfilled';
            this.refreshFormGrid();
          });
        break;
      }

      case 'pending':
      default: {
        // noop
        break;
      }
    }
  }

  refreshGrids() {
    switch (this.tab) {
      case 'addresses':
        this.refreshAddressesGrid();
        break;

      case 'memberships':
        this.refreshMembershipsGrid();
        break;

      case 'mergeHistory':
        this.refreshMergeHistoryGrid();
        break;

      case 'forms':
        this.refreshFormGrid();
        break;

      default:
        // noop
        break;
    }
  }

  processMemberships(memberships?: TODO[]): CustomerIndividualMembershipListItem[] {
    const membershipList = ensureArray(memberships).map((item) => ({
      ...item,
      membershipJoinedDateDisplay: this.calendarService.toFormat(
        this.calendarService.parseDate(item.membershipJoinedDate),
        this.userPreferencesService.getDateTimeFormat('sortDateTime'),
      ),
      membershipStartDateDisplay: this.calendarService.toFormat(
        this.calendarService.parseDate(item.membershipStartDate),
        this.userPreferencesService.getDateTimeFormat('sortDateTime'),
      ),
      membershipExpirationDateDisplay: this.calendarService.toFormat(
        this.calendarService.parseDate(item.membershipExpirationDate),
        this.userPreferencesService.getDateTimeFormat('sortDateTime'),
      ),
      isLatest: item.lastVersion ? 'Yes' : 'No',
    }));

    return this.customersIndividualsDetailService.groupCustomerMembershipsByMembershipNumber(membershipList);
  }

  createOrder() {
    // TODO: extract this logic to a reusable service
    // Call parent controller for this
    this.$scope.customersIndividuals.createOrder(this.model);
  }

  hasAddresses() {
    return Array.isArray(this.addresses) && this.addresses.length > 0;
  }

  getValidators() {
    const oneOf: TODO = this.getOneofValues();

    return [
      {
        field: 'customers-individuals-edit-first-name',
        len: { message: 'Invalid name', test: { value: this.customer.firstName } },
      },
      {
        field: 'customers-individuals-edit-last-name',
        len: { message: 'Invalid name', test: { value: this.customer.lastName } },
      },
      {
        field: 'customers-individuals-edit-email',
        optionalEmail: {
          message: 'Invalid email',
          test: () => {
            return this.customer.email;
          },
        },
      },
      {
        field: 'customers-individuals-edit-phone',
        optionalPhone: {
          message: 'Invalid phone number',
          test: () => {
            return this.customer.phoneNumber;
          },
        },
      },
    ].concat(oneOf);
  }

  getOneofValues() {
    const oneOfValues = [
      { value: this.customer.email },
      { value: this.customer.phoneNumber },
      { value: this.hasAddresses() },
    ];

    return [
      {
        field: 'customers-individuals-edit-email',
        oneOf: { message: 'Invalid email', test: { values: oneOfValues } },
      },
      {
        field: 'customers-individuals-edit-phone',
        oneOf: { message: 'Invalid phone number', test: { values: oneOfValues } },
      },
    ];
  }

  updateCustomer() {
    this.notice.hideErrorMessages();
    this.notice.hideSuccessMessages();

    this.loading = true;

    if (this.ValidateService.validate(this.getValidators())) {
      this.errors = {};
      this.customer.prefix = this.customer.selectedTitle?.title ?? '';

      this.customer.address = this.addresses;
      Object.assign(this.model, this.customer);

      this.apiService.customers
        .update(this.model)
        .then(() => {
          this.notice.showSuccessMessages([{ message: 'Successfully Updated!' }]);

          this.$timeout(() => {
            this.$state.go(
              'application.customers.individuals.detail',
              { id: this.getStateParams().id },
              { location: 'replace' },
            );
          }, 3000);
        })
        .catch((response) => {
          this.notice.showErrorMessages([{ message: extractACMEErrorMessage(response.data) }]);
        });
    } else {
      this.errors = this.ValidateService.getErrorHash();

      if (!this.ValidateService.validate(this.getOneofValues())) {
        this.notice.showErrorMessages([
          { message: 'To save a customer you must provide one of the following: Email, Phone, or Address' },
        ]);
      }
    }
  }

  fetchCustomer(params: { customerId: TODO }) {
    return this.apiService.customers
      .detail({
        customerId: params.customerId,
        includeAssociatedMemberships: true,
        includeMergeHistory: true,
      })
      .then(extractACMEPayload());
  }

  getCustomer() {
    this.loading = true;
    const params = this.getStateParams();

    if (!params.id) {
      return;
    }

    this.initHeader({ loading: true });

    this.fetchCustomer({ customerId: params.id })
      .then((customer) => {
        this.lookForDuplicates(customer);

        this.model = this.processModel(customer);
        this.customer = Object.assign({}, this.process(customer));

        this.addresses = this.model.address;
        this.memberships = this.processMemberships(this.model.memberships);
        this.attributes = this.model.attributes;
        this.solicitationCodes = this.model.solicitationCodes;

        if (this.isTab('')) {
          if (this.hasAddresses() || this.isEditMode()) {
            this.setTab('addresses');
          } else if (this.hasMemberships()) {
            this.setTab('memberships');
          } else if (this.hasAttributes()) {
            this.setTab('attributes');
          } else if (this.hasSolicitationCodes()) {
            this.setTab('solicitationCodes');
          } else if (this.hasMergeHistory()) {
            this.setTab('mergeHistory');
          }
        } else {
          this.refreshGrids();
        }
      })
      .catch((response) => {
        this.notice.showErrorMessages([{ message: extractACMEErrorMessage(response.data) }]);
      })
      .finally(() => {
        this.loading = false;
        this.initHeader({ loading: false });
      });
  }

  lookForDuplicates(customer: Customer) {
    const rawFilters = rawFiltersSchema.safeParse(customer);

    if (!this.enableCustomerMerge || rawFilters.success === false) {
      return this.$q.resolve({
        success: true,
        payload: {
          list: [],
          pagination: {
            count: 0,
            page: 1,
            pageSize: 0,
            sortDirection: 'desc',
            sortField: 'createdOn',
          },
        },
      } satisfies ACMEListResponse<Customer>);
    }

    return this.customersIndividualsListService
      .queryWithAdvancedSearch({
        pageSize: 50,
        page: 1,
        sortField: 'createdOn',
        sortDirection: 'desc',
        rawFilters: rawFilters.data,
      })
      .then((response) => {
        this.possiblyDuplicateCustomers = response.payload.list.filter((item) => item.id !== customer.id);

        return response;
      });
  }

  reviewPotentialMatches() {
    this.customersIndividualsListService.setAdvancedSearchState({
      lastName: this.customer.lastName,
      email: this.customer.email,
    });

    this.$state.go('application.customers.individuals.list');
  }

  showCancelModal() {
    this.$uibModal
      .open({
        size: 'sm',
        controller: 'ConfirmModalController',
        templateUrl: 'common/views/modals/confirm/confirm.tpl.html',
        resolve: {
          modalTitle: () => 'Are you sure?',
          okText: () => 'Continue',
          modalMessage: () => 'No changes will be saved.',
        },
      })
      .result.then(() => {
        this.$state.go(
          'application.customers.individuals.detail',
          { id: this.getStateParams().id },
          { location: 'replace' },
        );
      });
  }

  addAddress() {
    this.editAddress();
  }

  editAddress(address?: TODO) {
    this.$uibModal
      .open<TODO>({
        templateUrl: 'views/application/views/partners/create/modals/CreateEditCustomerAddressModal.tpl.html',
        size: 'md',
        backdrop: 'static',
        controller: 'CreateEditAddressModalController',
        resolve: {
          organizationId: () => this.getStateParams().accountId,
          address: () => address || null,
          preventSave: () => true,
        },
      })
      .result.then((address) => {
        if (address.id) {
          this.addresses.forEach((item: TODO) => {
            if (item.id === address.id) {
              Object.assign(item, address);
            }
          });
        } else {
          if (!this.addresses.length) {
            Object.assign(address, {
              type: 'primary',
              isPrimary: true,
            });
          }

          this.addresses.push(address);
        }

        this.refreshAddressesGrid();
      });
  }

  hasSolicitationCodes() {
    return Array.isArray(this.model.solicitationCodes) && this.model.solicitationCodes.length > 0;
  }

  hasMemberships() {
    return Array.isArray(this.model.memberships) && this.model.memberships.length > 0;
  }

  hasForms() {
    return Array.isArray(this.formsResponsesList) && this.formsResponsesList.length > 0;
  }

  hasAttributes() {
    return Array.isArray(this.model.attributes) && this.model.attributes.length > 0;
  }

  hasMergeHistory() {
    return Array.isArray(this.customer.mergeHistory) && this.customer.mergeHistory.length > 0;
  }

  process(customer: Customer): Customer & { selectedTitle?: { title: string; name: string } } {
    return {
      ...customer,
      prefix: customer.prefix || '',
      suffix: customer.suffix || '',
      middleName: customer.middleName || '',
      phoneNotes: customer.phoneNotes || '',
      selectedTitle: this.customerService.findSalutation(customer.prefix || ''),
      mergeHistory: customer.mergeHistory ?? [],
    };
  }

  processModel(model: TODO) {
    return {
      ...model,
      createdOn: this.calendarService.toFormat(this.calendarService.parseDate(model.createdOn), this.dateTimeFormat),
      updatedOn: this.calendarService.toFormat(this.calendarService.parseDate(model.updatedOn), this.dateTimeFormat),
      createdBy:
        model.createdBy && this.permissionsService.check('USERS_VIEW')
          ? parseUrl(this.$state.href('application.users.detail', { id: model.createdBy }), model.createdBy)
          : null,
      updatedBy:
        model.updatedBy && this.permissionsService.check('USERS_VIEW')
          ? parseUrl(this.$state.href('application.users.detail', { id: model.updatedBy }), model.updatedBy)
          : null,
    };
  }

  getFormsResponses(formsList: CustomerFormResponse[]) {
    return this.customersIndividualsDetailService
      .getFormMetaData(formsList)
      .then((membershipData) => this.processFormsList(formsList, membershipData));
  }

  processFormsList(formsList: CustomerFormResponse[], membershipsData: TODO) {
    const formsSummariesListItemList = formsList.map((item) => {
      switch (item.formCategoryType) {
        case 'membership': {
          return {
            ...item,
            itemName: '',
            membershipLifecycle: this.customersIndividualsDetailService.getMembershipLifecycle(item.itemType),
            ...(item.membershipId
              ? {
                  membershipId: item.membershipId,
                  membershipLevel: membershipsData[item.membershipId].membershipLevel,
                  membershipOffering: membershipsData[item.membershipId].membershipOffering,
                }
              : {}),
          };
        }

        case 'event':
        case 'donation':
        case 'addon':
        default: {
          return item;
        }
      }
    });

    return formsSummariesListItemList;
  }

  refreshAddressesGrid() {
    const totalItems = this.addresses ? this.addresses.length : 0;
    this.addressGridDefinition.isLoaded = true;

    this.$timeout(() => {
      this.$scope.$broadcast('DataGrid::ResizeGrid', {
        name: this.addressGridDefinition.options.gridName,
        datasetLength: totalItems,
        totalItems: totalItems,
      });
    }, 10);
  }

  refreshMembershipsGrid() {
    const totalItems = this.memberships ? this.memberships.length : 0;
    this.membershipGridDefinition.isLoaded = true;

    this.$timeout(() => {
      this.$scope.$broadcast('DataGrid::ResizeGrid', {
        name: this.membershipGridDefinition.options.gridName,
        datasetLength: totalItems,
        totalItems: totalItems,
      });
    }, 10);
  }

  refreshMergeHistoryGrid() {
    const totalItems = this.customer.mergeHistory ? this.customer.mergeHistory.length : 0;
    this.mergeHistoryGridDefinition.isLoaded = true;
    this.$timeout(() => {
      this.$scope.$broadcast('DataGrid::ResizeGrid', {
        name: this.mergeHistoryGridDefinition.options.gridName,
        datasetLength: totalItems,
        totalItems: totalItems,
      });
    }, 10);
  }

  refreshFormGrid() {
    const totalItems = this.formsResponsesList ? this.formsResponsesList.length : 0;
    this.formsGridDefinition.isLoaded = true;
    this.$timeout(() => {
      this.$scope.$broadcast('DataGrid::ResizeGrid', {
        name: this.formsGridDefinition.options.gridName,
        datasetLength: totalItems,
        totalItems: totalItems,
      });
    }, 10);
  }

  onIdClick(item) {
    this.$state.go('application.membership.detail', { id: item.id });
  }

  onSetAsLatest(item: TODO) {
    if (item.membershipStanding !== 'new') {
      this.notice.hideErrorMessages();
      this.notice.hideSuccessMessages();

      this.isMembershipLoading = true;

      this.apiService.membership.versions
        .setLatest(item.id)
        .then(() => {
          this.notice.showSuccessMessages([{ message: 'Membership Updated!' }], { duration: 3000 });

          this.getCustomer();
          this.isMembershipLoading = false;
        })
        .catch((response) => {
          this.notice.showErrorMessages([{ message: extractACMEErrorMessage(response.data) }], { duration: 3000 });
          this.isMembershipLoading = false;
        });
    }
  }

  onFormsFilterDateTypeChange(formsSelectedFilterDateType: { type: string; name: string }) {
    this.formsQueryParams.filterType = formsSelectedFilterDateType.type;
  }

  private setDateTimeFormat() {
    this.dateTimeFormat = this.userPreferencesService.getDateTimeFormat('sortDateTime');
    this.formsGridOptions.optionsOverride.appScopeProvider.dateTimeFormat = this.dateTimeFormat;
  }
}

export const CustomersIndividualsDetailComponentModule = angular
  .module('acme.view.application.customers.individuals.detail.controllers', [
    uiRouter,
    'ui.bootstrap',
    'acme.common.service.api',
    'acme.common.service.permissions',
    'acme.common.service.themesHelper',
    'acme.common.service.calendar',
    CustomerServiceModule,
    ContentHeaderServiceModule,
    'acme.common.service.userPreferences',
    'acme.common.service.validate',
    'acme.common.factory.notice',
    'acme.common.factory.gridState',
    'acme.common.factory.gridDefinition',
    CustomersIndividualsListServiceModule,
    CustomersIndividualsDetailServiceModule,
    NoticesComponentModule,
    FormatDateFilterModule,
  ])
  .component('acmeCustomersIndividualsDetail', {
    templateUrl: 'views/application/views/customers/individuals/detail/detail.tpl.html',
    controller: CustomersIndividualsDetailController,
    bindings: {
      ThemesData: '<',
    },
  }).name;
